import React, { useState, useEffect } from 'react';
import { ShoppingCart, MessageCircle, CircleUserRound, UsersRound} from 'lucide-react';
import { useNavigate, Link } from "react-router-dom";
import axios from 'axios';
import logo from '../images/Header/Logo.svg';
import { GlobeAltIcon } from '@heroicons/react/24/solid';
import { useLanguage } from "./LanguageContext";
import { Translator } from './translator';

const BASE = "https://xmrhe4snn1.execute-api.eu-north-1.amazonaws.com";

const Header = ({ user }) => {
  const [headerUser, setHeaderUser] = useState(user || null);
  const { lang, setLang } = useLanguage();
  const [profileOpen, setProfileOpen] = useState(false);
  const [showMetaverseModal, setShowMetaverseModal] = useState(false);
  const { translateText } = Translator();
  const navigate = useNavigate();
  const isLoggedIn = !!headerUser;
  const [preDownload, setPreDownload] = useState(null);
  const [unreadMessages, setUnreadMessages] = useState(0);
  const [pendingRequests, setPendingRequests] = useState(0);
  const [closeTimeout, setCloseTimeout] = useState(null);
  const [cartPopupVisible, setCartPopupVisible] = useState(false);
  const [avatarUrl, setAvatarUrl] = useState(null);
  const [popupTimer, setPopupTimer] = useState(null);
  const [cartCount, setCartCount] = useState(0);
  const [hasPlan, setHasPlan] = useState(false);
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const [showAvatarHint, setShowAvatarHint] = useState(false);
  const BASE_LANGS = ["en", "it", "fr"];
  const LANG_LABELS = { en:"English", it:"Italiano", fr:"Française", ab:"Abkhaz", aa:"Afar", ar:"العربية", zh:"中文", es:"Español", de:"Deutsch", ru:"Русский", pt:"Português", ja:"日本語", hi:"हिन्दी", bn:"বাংলা", pa:"ਪੰਜਾਬੀ", jv:"Basa Jawa", ko:"한국어", vi:"Tiếng Việt", tr:"Türkçe", nl:"Nederlands", sv:"Svenska", no:"Norsk", da:"Dansk", fi:"Suomi", el:"Ελληνικά", hu:"Magyar", cs:"Čeština", ro:"Română", sk:"Slovenčina", uk:"Українська", id:"Bahasa Indonesia", ms:"Bahasa Melayu", th:"ไทย", fa:"فارسی", ur:"اردو", ca:"Català", hr:"Hrvatski", lt:"Lietuvių", sl:"Slovenščina", et:"Eesti", lv:"Latviešu", af:"Afrikaans", is:"Íslenska", sq:"Shqip", am:"አማርኛ", an:"Aragonés", hy:"Հայերեն", as:"অসমীয়া", ae:"Avesta", ay:"Aymar aru", az:"Azərbaycan dili", ba:"Башҡортса", eu:"Euskara", be:"Беларуская", bh:"भोजपुरी", bi:"Bislama", bs:"Bosanski", br:"Brezhoneg", bg:"Български", my:"မြန်မာစာ", ch:"Chamoru", ce:"Нохчийн мотт", cu:"Ѩзыкъ словѣньскъ", cv:"Чӑвашла", kw:"Kernewek", co:"Corsu", dv:"ދިވެހި", dz:"རྫོང་ཁ", eo:"Esperanto", fo:"Føroyskt", fj:"Vosa Vakaviti", gd:"Gàidhlig", gl:"Galego", ka:"ქართული", gn:"Avañe'ẽ", gu:"ગુજરાતી", ht:"Kreyòl ayisyen", ha:"Hausa", he:"עברית", hz:"Otsiherero", ho:"Hiri Motu", ia:"Interlingua", io:"Ido", iu:"ᐃᓄᒃᑎᑐᑦ", ik:"Iñupiatun", ga:"Gaeilge", kl:"Kalaallisut", kn:"ಕನ್ನಡ", ks:"कश्मीरी", kk:"Қазақ тілі", km:"ខ្មែរ", ki:"Gĩkũyũ", rw:"Kinyarwanda", ky:"Кыргызча", kv:"Коми кыв", kj:"Kuanyama", ku:"Kurdî", lo:"ລາວ", la:"Latina", li:"Limburgs", ln:"Lingála", lb:"Lëtzebuergesch", mk:"Македонски", mg:"Malagasy", ml:"മലയാളം", mt:"Malti", gv:"Gaelg", mi:"Māori", mr:"मराठी", mh:"Kajin M̧ajeļ", mo:"Moldovenească", mn:"Монгол", na:"Dorerin Naoero", nv:"Diné Bizaad", nd:"isiNdebele", nr:"isiNdebele (South)", ng:"Owambo", ne:"नेपाली", se:"Davvisámegiella", nb:"Norsk Bokmål", nn:"Norsk Nynorsk", ny:"ChiCheŵa", oc:"Occitan", or:"ଓଡିଆ", om:"Oromoo", os:"Ирон", pi:"पालि", ps:"پښتو", qu:"Runasimi", rm:"Rumantsch", rn:"Ikirundi", sm:"Gagana Samoa", sg:"Sängö", sa:"संस्कृतम्", sc:"Sardu", sr:"Српски", sn:"ChiShona", ii:"ꆈꌠ꒿", sd:"سنڌي", si:"සිංහල", so:"Soomaaliga", st:"Sesotho", su:"Basa Sunda", sw:"Kiswahili", ss:"SiSwati", tl:"Tagalog", ty:"Reo Tahiti", tg:"Тоҷикӣ", ta:"தமிழ்", tt:"Татарча", te:"తెలుగు", bo:"བོད་ཡིག", ti:"ትግርኛ", to:"lea fakatonga", ts:"Xitsonga", tn:"Setswana", tk:"Türkmençe", tw:"Twi", ug:"ئۇيغۇرچە", uz:"O‘zbek", vo:"Volapük", wa:"Walon", cy:"Cymraeg", fy:"Frysk", wo:"Wolof", xh:"isiXhosa", yi:"ייִדיש", yo:"Yorùbá", za:"Vahcuengh", zu:"isiZulu" };



  // figure out if current lang is one of the fixed ones
  const isBaseLang = BASE_LANGS.includes(lang);

  // what value the <select> should use
  const effectiveLangValue = lang || "en";

  // extra option only if lang is NOT en/it/fr
  const extraLangOption = !isBaseLang && lang ? lang : null;

  // label for that extra option (fallback: show the code in brackets)
  const extraLangLabel =
    extraLangOption && LANG_LABELS[extraLangOption]
      ? LANG_LABELS[extraLangOption]
      : extraLangOption
      ? `(${extraLangOption})`
      : "";


  const handleHeaderLangChange = async (e) => {
    const newLang = e.target.value;

    // if same language, do nothing
    if (newLang === lang) return;

    // 1) update context + localStorage
    setLang(newLang);

    // 2) read user from localStorage
    let storedUser = null;
    try {
      storedUser = JSON.parse(localStorage.getItem("user") || "null");
    } catch {
      storedUser = null;
    }

    // 3) If NOT logged in → just reload the site so everything re-renders in new lang
    if (!storedUser || !storedUser.email || !storedUser.password) {
      window.location.reload();
      return;
    }

    // 4) If logged in → also update backend preferredLanguage, then reload
    try {
      await updatePreferredLanguageOnBackend(newLang, storedUser);
    } catch (err) {
      console.error("Error updating preferredLanguage from header:", err);
      // even if backend fails, we still want UI to reflect new language
    }

    // 5) Finally: reload whole SPA so all pages/components pick new lang
    window.location.reload();
  };



  const updatePreferredLanguageOnBackend = async (newLang, storedUser) => {
    if (!storedUser || !storedUser.email || !storedUser.password) return;

    const clean = (v) =>
      v == null || String(v).trim().toUpperCase() === "N/A" ? "" : String(v);

    const url =
      `${BASE}/changeTUserData` +
      `?email=${encodeURIComponent(storedUser.email)}` +
      `&password=${encodeURIComponent(storedUser.password)}` +
      `&newfirstname=${encodeURIComponent(clean(storedUser.firstName))}` +
      `&newlastname=${encodeURIComponent(clean(storedUser.lastName))}` +
      `&newphone=${encodeURIComponent(clean(storedUser.telefono))}` +
      `&newaddress=${encodeURIComponent(clean(storedUser.indirizzo))}` +
      `&newcap=${encodeURIComponent(clean(storedUser.cap))}` +
      `&newcountry=${encodeURIComponent(clean(storedUser.nazione))}` +
      `&newgender=${encodeURIComponent(clean(storedUser.gender))}` +
      `&newlang=${encodeURIComponent(newLang)}`;

    const res = await fetch(url, { method: "GET" });
    const result = await res.json();
    if (result === "0") {
      console.warn("Failed to update preferredLanguage.");
      return;
    }

    const updatedUser = {
      ...storedUser,
      preferredLanguage: newLang,
    };
    localStorage.setItem("user", JSON.stringify(updatedUser));

    setHeaderUser(updatedUser);

    window.dispatchEvent(
      new CustomEvent("auth:login", { detail: updatedUser })
    );
  };


  const baseTextByLang = {
    it: {
      text1: "Home",
      text2: "Chi siamo",
      text3: "Tour Digitali",
      text4: "FuoriRotta",
      text5: "Community",
      text6: "Blog",
      text7: "NFTteche",
      text8: "Assistenza",
      text9: "Carrello",
      text10: "Account",
      text11: "Login",
      text12: "Registrati",
      text13: "Il mio Profilo",
      text14: "Esci",
      text15: "Tour Acquistati",
      text16: "Pagamento sicuro",
      text17: "Nessun costo nascosto",
      text18: "Servizio clienti disponibile H24 in tutto il mondo",
      textRichieste: "Amici",
      textMessaggi: "Messaggi",
      textPerche:"Perché acquistare il nostro prodotto?",
      textwhy: "Perché non inserisci un'immagine nel tuo profilo?",
      metaverseDownload: "Scarica l'app Metaverse",
      metaverseModalTitle: "Scarica l'app Metaverse",
      metaverseAndroid: "Android",
      metaversePC: "PC (Windows)",
      metaverseClose: "Chiudi",
      androidModalTitle: "Prima di scaricare l’app Android",
      androidModalBody:
        "La nostra app è affidabile e ha superato controlli di sicurezza interni. Se il tuo dispositivo ti avvisa che il file proviene da una fonte esterna, puoi confermare in sicurezza: il pacchetto è stato verificato. Per qualsiasi problema, contattaci: emozionartisrl@gmail.com.",
      pcModalTitle: "Prima di scaricare su PC",
      pcModalBody:
        "Il file è compresso in formato .rar. Dopo il download, estrailo (UnRAR/WinRAR/7-Zip) in una cartella a tua scelta e avvia l’app Metaverse “Metaverse.exe”. Se l’antivirus mostra avvisi, consenti l’esecuzione solo se stai scaricando da techetour.it. Per assistenza: emozionartisrl@gmail.com.",
      downloadNow: "Scarica ora",
      cancel: "Annulla",

    },

    en: {
      text1: "Home",
      text2: "About us",
      text3: "Digital Tours",
      text4: "Off-Route",
      text5: "Community",
      text6: "Blog",
      text7: "NFTteche",
      text8: "Support",
      text9: "Cart",
      text10: "Account",
      text11: "Login",
      text12: "Register",
      text13: "My Profile",
      text14: "Logout",
      text15: "Purchased Tours",
      text16: "Secure payment",
      text17: "No hidden fees",
      text18: "24/7 global customer support",
      textRichieste: "Friends",
      textMessaggi: "Messages",
      textPerche:"Why buy our product?",
      textwhy:"Want to set an image in your profile?",
      metaverseDownload: "Download Metaverse App",
      metaverseModalTitle: "Download Metaverse App",
      metaverseAndroid: "Android",
      metaversePC: "PC (Windows)",
      metaverseClose: "Close",
      androidModalTitle: "Before downloading the Android app",
      androidModalBody:"Our app is reliable and has undergone internal security checks. If your device warns you that the file comes from an external source, you can safely confirm: the package has been verified. For any problem, contact us at emozionartisrl@gmail.com.",
      pcModalTitle: "Before downloading on PC",
      pcModalBody:"The file is compressed in .rar format. After downloading, extract it (UnRAR/WinRAR/7-Zip) into a folder of your choice and launch the Metaverse app “Metaverse.exe”. If your antivirus shows warnings, allow the execution if you are downloading from techetour.it. For assistance, write to emozionartisrl@gmail.com.",

    },

    fr: {
      text1: "Accueil",
      text2: "À propos",
      text3: "Tours Numériques",
      text4: "HorsRoute",
      text5: "Community",
      text6: "Blog",
      text7: "NFTteche",
      text8: "Assistance",
      text9: "Panier",
      text10: "Compte",
      text11: "Connexion",
      text12: "Inscription",
      text13: "Mon Profil",
      text14: "Déconnexion",
      text15: "Tours Achetés",
      text16: "Paiement sécurisé",
      text17: "Aucun frais caché",
      text18: "Service client 24h/24 dans le monde entier",
      textRichieste: "Amis",
      textMessaggi: "Messages",
      textPerche:"Pourquoi acheter notre produit?",
      textwhy:"Pourquoi ne pas mettre une image dans votre profil?",
      metaverseDownload: "Télécharger l’application Metaverse",
      metaverseModalTitle: "Télécharger l’application Metaverse",
      metaverseAndroid: "Android",
      metaversePC: "PC (Windows)",
      metaverseClose: "Fermer",
      androidModalTitle: "Avant de télécharger l’application Android",
      androidModalBody: "Notre application est fiable et a passé des contrôles de sécurité internes. Si votre appareil vous avertit que le fichier provient d’une source externe, vous pouvez confirmer en toute sécurité : le paquet a été vérifié. En cas de problème, contactez-nous : emozionartisrl@gmail.com.",
      pcModalTitle: "Avant de télécharger sur PC",
      pcModalBody: "Le fichier est compressé au format .rar. Après le téléchargement, extrayez-le (UnRAR/WinRAR/7-Zip) dans un dossier de votre choix puis lancez l’app Metaverse “Metaverse.exe”. Si l’antivirus affiche des avertissements, autorisez l’exécution uniquement si vous téléchargez depuis techetour.it. Assistance : emozionartisrl@gmail.com.",
      downloadNow: "Télécharger maintenant",
      cancel: "Annuler",

    }
  };

  const activeBaseText = baseTextByLang[lang] || baseTextByLang.it;
  const [translatedText, setTranslatedText] = useState(activeBaseText);

    useEffect(() => {
    let cancelled = false;

    // 1️⃣ For Italian / English / French → use static texts, no API
    if (lang === "it" || lang === "en" || lang === "fr") {
      setTranslatedText(baseTextByLang[lang] || baseTextByLang.it);
      return;
    }

    // 2️⃣ For ALL OTHER languages (Arabic, etc.) → use translation API
    async function translateAll() {
      try {
        // Use Italian as the source for translations
        const source = baseTextByLang.it;
        const entries = Object.entries(source);

        const translatedEntries = await Promise.all(
          entries.map(async ([key, value]) => {
            if (!value) return [key, value];
            const out = await translateText(value);
            return [key, out || value]; // fallback to Italian if empty
          })
        );

        if (!cancelled) {
          setTranslatedText(Object.fromEntries(translatedEntries));
        }
      } catch (err) {
        if (!cancelled) {
          // On error, fall back to Italian strings
          setTranslatedText(baseTextByLang.it);
        }
      }
    }

    translateAll();

    // Avoid setting state if component unmounts
    return () => {
      cancelled = true;
    };
  }, [lang, translateText]);



  useEffect(() => {
    setHeaderUser(user || null);
  }, [user]);

  useEffect(() => {
    const readUser = () => {
      try {
        return JSON.parse(localStorage.getItem("user") || "null");
      } catch {
        return null;
      }
    };

    const handleLogin = (e) => {
      const u = e?.detail || readUser();
      setHeaderUser(u || null);
    };

    const handleLogout = () => {
      setHeaderUser(null);
      setUnreadMessages(0);
      setPendingRequests(0);
      setCartCount(0);
      setAvatarUrl(null);
    };

    window.addEventListener("auth:login", handleLogin);
    window.addEventListener("auth:logout", handleLogout);

    return () => {
      window.removeEventListener("auth:login", handleLogin);
      window.removeEventListener("auth:logout", handleLogout);
    };
  }, []);


  useEffect(() => {
    const handleShowPopup = () => {
      setCartPopupVisible(true);
      if (popupTimer) clearTimeout(popupTimer);
      const timer = setTimeout(() => setCartPopupVisible(false), 5000);
      setPopupTimer(timer);
    };

    window.addEventListener("showCartPopup", handleShowPopup);
    return () => window.removeEventListener("showCartPopup", handleShowPopup);
  }, [popupTimer]);


  useEffect(() => {
    // Prefer the prop "user", fallback to localStorage
    let effectiveUser = headerUser
    if (!effectiveUser) {
      try {
        effectiveUser = JSON.parse(localStorage.getItem("user") || "null");
      } catch {
        effectiveUser = null;
      }
    }

    // Normalize the raw value (can be undefined, null, or missing)
    const rawPic =
      typeof effectiveUser?.profilePic === "string"
        ? effectiveUser.profilePic.trim()
        : "";

    // Treat missing / placeholder / invalid as "no image"
    const isMissingOrPlaceholder =
      !rawPic ||
      rawPic === "./avatar.png" ||
      rawPic === "../avatar.png" || 
      rawPic === "../images/Header/avatar.png" ||
      rawPic === "/avatar.png" ||
      rawPic === "undefined" ||
      rawPic === "null";

    if (isMissingOrPlaceholder) {
      // No valid image → use only the <CircleUserRound> icon
      setAvatarUrl(null);
      console.log("No valid profilePic found for header avatar -> using icon only.");
    } else {
      // Valid custom image: build full URL if needed
      const fullPicUrl = rawPic.startsWith("http")
        ? rawPic
        : `${BASE}/media/${rawPic.replace(/^\/+/, "")}`;

      setAvatarUrl(fullPicUrl);
      console.log("Header avatar URL:", fullPicUrl);
    }
  }, [headerUser]);



  useEffect(() => {
    const onAvatarChanged = (e) => {
      const url = e?.detail?.profilePic;
      if (url) {
        setAvatarUrl(url); // instantly update header image
      }
    };

    window.addEventListener("auth:avatarChanged", onAvatarChanged);
    return () => window.removeEventListener("auth:avatarChanged", onAvatarChanged);
  }, []);


  useEffect(() => {
    const fetchCartCount = async () => {
      const storedUser = localStorage.getItem("user");
      if (!storedUser) {
        setCartCount(0);
        return;
      }

      try {
        const user = JSON.parse(storedUser);
        const res = await axios.get(
          `${BASE}/getCart?email=${encodeURIComponent(user.email)}`
        );

        const cart = Array.isArray(res.data?.cart) ? res.data.cart : [];
        const plan = res.data?.plan || null;

        setHasPlan(!!plan);

        const toursQty = cart.reduce(
          (sum, item) => sum + (parseInt(item.quantity, 10) || 1),
          0
        );
        const planCount = plan ? 1 : 0;

        setCartCount(toursQty + planCount);
      } catch (e) {
        setCartCount(0);
      }
    };

    fetchCartCount();

    const handleUpdate = () => fetchCartCount();

    window.addEventListener("showCartPopup", handleUpdate);
    window.addEventListener("auth:login", handleUpdate);
    return () => {
      window.removeEventListener("showCartPopup", handleUpdate);
      window.removeEventListener("auth:login", handleUpdate);
    };
  }, []);


  // fetch unread counts
  useEffect(() => {
    const me = headerUser || (() => {
      try { return JSON.parse(localStorage.getItem("user") || "null");}
      catch { return null; }
    })();
    if (!me?.email) return;

    async function fetchCounts() {
      try {
        // unread messages
        const convRes = await fetch(
          `${BASE}/conversations_list?email=${encodeURIComponent(me.email)}`
        );
        const convJson = await convRes.json();
        const unread = (convJson.items || []).filter(c => c.unread > 0).length;
        setUnreadMessages(unread);
      } catch (e) {
        setUnreadMessages(0);
      }

      try {
        // friend requests
        const frRes = await fetch(
          `${BASE}/friends_requests?email=${encodeURIComponent(me.email)}`
        );
        const frJson = await frRes.json();
        setPendingRequests((frJson.incoming || []).length);
      } catch (e) {
        setPendingRequests(0);
      }
    }

    // initial load
    fetchCounts();

    // update when /friends page dispatches "friends:updated"
    const onFriendsUpdated = (e) => {
      const c = e?.detail?.incomingCount;
      if (typeof c === "number") {
        setPendingRequests(c);
      } else {
        // fallback – re-fetch
        fetchCounts();
      }
    };

    // re-fetch on global login event
    const onAuthLogin = () => {
      fetchCounts();
    };

    window.addEventListener("friends:updated", onFriendsUpdated);
    window.addEventListener("auth:login", onAuthLogin);

    // optional: poll every 30s to catch new requests from other devices
    const interval = setInterval(fetchCounts, 30000);

    return () => {
      window.removeEventListener("friends:updated", onFriendsUpdated);
      window.removeEventListener("auth:login", onAuthLogin);
      clearInterval(interval);
    };
  }, [headerUser]);


  // Show avatar hint for 10s, from time to time (only if logged in and no avatar)
  useEffect(() => {
    if (!isLoggedIn || avatarUrl) {
      setShowAvatarHint(false);
      return;
    }

    let hideTimeout = null;

    const triggerHint = () => {
      setShowAvatarHint(true);
      if (hideTimeout) clearTimeout(hideTimeout);
      hideTimeout = setTimeout(() => setShowAvatarHint(false), 10000); // 10 seconds
    };

    // show once immediately
    triggerHint();

    // then occasionally (every 60s)
    const intervalId = setInterval(triggerHint, 60000);

    return () => {
      if (hideTimeout) clearTimeout(hideTimeout);
      clearInterval(intervalId);
    };
  }, [isLoggedIn, avatarUrl]);




  useEffect(() => {
    // 1) Make sure user is logged in at all
    let currentUser = null;
    try {
      currentUser = JSON.parse(localStorage.getItem("user") || "null");
    } catch {
      currentUser = null;
    }

    if (!currentUser?.email) {
      return; // not logged in
    }

    // 2) IF user already has preferredLanguage, do nothing
    if (currentUser.preferredLanguage) {
      return;
    }

    // helper: set in context + DB
    const applyLang = async (code) => {
      if (!code) return;
      const normalized = code.split(/[-_]/)[0].toLowerCase();

      setLang(normalized);

      try {
        await updatePreferredLanguageOnBackend(normalized, currentUser);
        // make sure Header state is fresh too
        setHeaderUser((prev) => ({
          ...(prev || {}),
          preferredLanguage: normalized,
        }));
      } catch (err) {
        console.error("auto preferredLanguage update failed:", err);
      }
    };

    // 3) Try browser language
    let detected = null;
    if (typeof navigator !== "undefined") {
      const navLangs =
        (navigator.languages && navigator.languages.length
          ? navigator.languages
          : [navigator.language]) || [];
      const first = (navLangs[0] || "").toString();
      if (first) detected = first;
    }

    if (detected) {
      applyLang(detected);
      return;
    }

    // 4) Fallback: default to English if nothing detected
    applyLang("en");
  }, [setLang, updatePreferredLanguageOnBackend]);





  // Open ChatFloat via global event
  const openMessages = () => {
    window.dispatchEvent(new CustomEvent("chat:open", { detail: {} }));
  };


  const openMetaverseModal = () => {
    setShowMetaverseModal(true);
  };

  const closeMetaverseModal = () => {
    setShowMetaverseModal(false);
  };

  const downloadAndroid = () => {
    setPreDownload(null);
    setShowMetaverseModal(false);
    setMobileMenuOpen(false);
    setTimeout(() => {
      window.location.href = "/MetaverseApps/Teche_Metaverse.apk";
    }, 0);
  };

  const downloadPC = () => {
    setPreDownload(null);
    setShowMetaverseModal(false);
    setMobileMenuOpen(false);
    setTimeout(() => {
      window.location.href = "/MetaverseApps/Teche_Metaverse.rar";
    }, 0);
  };




  return (
    <>
      <style>
        {`
          @keyframes avatar-beat {
            0%, 100% { transform: scale(1); }
            25% { transform: scale(1.12); }
            50% { transform: scale(0.95); }
            75% { transform: scale(1.08); }
          }
          .avatar-beat {
            animation: avatar-beat 0.9s ease-in-out infinite;
          }

          @keyframes hint-fade-in {
            from { opacity: 0; transform: translateY(-4px); }
            to   { opacity: 1; transform: translateY(0); }
          }
          .animate-fade-in {
            animation: hint-fade-in 0.25s ease-out;
          }
        `}
      </style>
    <header className="sticky top-0 z-[10000] bg-white shadow-md w-full">
      {/* max width wrapper */}
      <div className="w-full px-3 sm:px-4 lg:px-6">
        {/* 3 columns: logo | center nav | right controls */}
        <div className="grid grid-cols-[auto_1fr_auto] items-center h-16 sm:h-18 lg:h-20">
          {/* Left: Logo */}
          <Link to="/" className="flex items-center col-start-1 justify-self-start">
            <img src={logo} alt="Logo" className="h-10 sm:h-12 lg:h-16 w-auto object-contain" />
          </Link>

          {/* Center: Navigation (desktop only) */}
          <nav className="hidden lg:flex justify-center items-center gap-5 whitespace-nowrap col-start-2 ml-2">
            <Link to="/"        className="no-underline text-black font-semibold text-base xl:text-lg hover:no-underline">{translatedText.text1}</Link>
            <Link to="/about"   className="no-underline text-black font-semibold text-base xl:text-lg hover:no-underline">{translatedText.text2}</Link>
            <Link to="/choose"  className="no-underline text-black font-semibold text-base xl:text-lg hover:no-underline">{translatedText.text3}</Link>
            <Link to="/FuoriPreview/127981" className="no-underline text-black font-semibold text-base xl:text-lg hover:no-underline">{translatedText.text4}</Link>
            <Link to="/community"  className="no-underline text-black font-semibold text-base xl:text-lg hover:no-underline">{translatedText.text5}</Link>
          {/*  <Link to="/blog"    className="no-underline text-black font-semibold text-base xl:text-lg hover:no-underline">{translatedText.text6}</Link>  */}
          {/*  <Link to="/nft"     className="no-underline text-black font-semibold text-base xl:text-lg hover:no-underline">{translatedText.text7}</Link>  */}
            <Link to="/support" className="no-underline text-black font-semibold text-base xl:text-lg hover:no-underline">{translatedText.text8}</Link>
          </nav>

          {/* Right: Controls */}
          <div className="col-start-3 justify-self-end flex items-center gap-4">
            {/* Friend Requests Icon (desktop) */}
            {isLoggedIn && (
              <button
                onClick={() => navigate("/friends")}
                className="hidden lg:flex flex-col items-center relative text-black hover:opacity-80"
              >
                <UsersRound className="w-8 h-8" />
                {pendingRequests > 0 && (
                  <span className="absolute -top-1 -right-1 bg-red-500 text-white text-[10px] w-4 h-4 flex items-center justify-center rounded-full">
                    {pendingRequests}
                  </span>
                )}
                <span className="text-sm mt-0.5">{translatedText.textRichieste}</span>
              </button>
            )}

            {/* Messages Icon (desktop) */}
            {isLoggedIn && (
              <button
                onClick={() => navigate("/messages")}
                className="hidden lg:flex flex-col items-center relative text-black hover:opacity-80"
              >
                <div className="relative">
                  <MessageCircle className="w-8 h-8" />
                  {unreadMessages > 0 && (
                    <span className="absolute -top-1 -right-1 flex h-4 w-4">
                      <span className="animate-ping absolute inline-flex h-full w-full rounded-full bg-red-400 opacity-75" />
                      <span className="relative inline-flex rounded-full h-4 w-4 bg-red-500 text-[10px] text-white items-center justify-center">
                        {unreadMessages}
                      </span>
                    </span>
                  )}
                </div>
                <span className="text-sm mt-0.5">{translatedText.textMessaggi}</span>
              </button>
            )}
            
            {/* Language – horizontal on mobile, vertical on lg+ */}
            <div className="hidden lg:flex items-center gap-1 lg:flex-col lg:gap-0">
              <GlobeAltIcon className="h-7 w-7 text-black lg:h-8 lg:w-8" />
              <select
                className="text-xs sm:text-sm border-none outline-none bg-transparent cursor-pointer text-black"
                value={effectiveLangValue}
                onChange={handleHeaderLangChange}
              >
                {/* fixed base languages */}
                <option value="en">{LANG_LABELS.en}</option>
                <option value="it">{LANG_LABELS.it}</option>
                <option value="fr">{LANG_LABELS.fr}</option>

                {/* dynamic extra language – only if user chose something else in Profile */}
                {extraLangOption && (
                  <option value={extraLangOption}>{extraLangLabel}</option>
                )}

              </select>
            </div>



            {/* Cart (desktop only) */}
            <Link to="/carrello" className="hidden lg:flex no-underline flex-col items-center gap-1 hover:no-underline">
              <div className="relative">
                <ShoppingCart className="text-black w-8 h-8" />
                {cartCount > 0 && (
                  <span className="absolute -top-1 -right-1 bg-red-500 text-white text-[10px] w-4 h-4 rounded-full flex items-center justify-center">
                    {cartCount}
                  </span>
                )}
              </div>
              <span className="text-sm text-black">{translatedText.text9}</span>
            </Link>


            


            {/* Profile (desktop only) */}
            <div
              className="hidden lg:block relative"
              onMouseEnter={() => { if (closeTimeout) clearTimeout(closeTimeout); setProfileOpen(true); }}
              onMouseLeave={() => { const t = setTimeout(() => setProfileOpen(false), 300); setCloseTimeout(t); }}
            >
            <button className="flex flex-col items-center gap-1 text-black">
              <div className="w-8 h-8 rounded-full overflow-hidden border border-gray-300 bg-gray-100 flex items-center justify-center">
                {avatarUrl ? (
                  <img
                    src={avatarUrl}
                    alt="Profilo utente"
                    className="w-full h-full object-cover"
                    onError={() => setAvatarUrl(null)}
                  />
                ) : (
                  <CircleUserRound className={`w-8 h-8 text-black/80 ${showAvatarHint ? "avatar-beat" : ""}`} />
                )}
              </div>
              <span className="text-sm">{translatedText.text10}</span>
            </button>


              {isLoggedIn && !avatarUrl && showAvatarHint && (
                <div className="hidden sm:block absolute left-1/2 top-full -translate-x-1/2 mt-3 z-[10500]">
                  <div className="relative animate-fade-in">
                    
                    {/* Arrow pointing up */}  
                    <div className="absolute left-1/2 -top-1 -translate-x-1/2 w-3 h-3 bg-white border-l border-t border-gray-200 rotate-45 shadow-sm" />

                    {/* Card */}
                    <div className="max-w-[240px] bg-white border border-gray-200 shadow-xl rounded-xl px-3 py-2 text-center">
                      <p className="text-[11px] leading-snug text-gray-700 mt-0.5">
                        {translatedText.textwhy}
                      </p>
                    </div>

                  </div>
                </div>
              )}




              {profileOpen && (
                <div className="absolute right-0 mt-2 w-48 bg-white border rounded-lg shadow-lg text-black z-[10100]">
                  {headerUser ? (
                    <>
                      <Link to="/profile" className="block px-4 py-2 text-sm hover:bg-gray-100 no-underline">{translatedText.text13}</Link>
                      <Link to="/myTours" className="block px-4 py-2 text-sm hover:bg-gray-100 no-underline">{translatedText.text15}</Link>
                      {hasPlan && (
                        <button
                          onClick={openMetaverseModal}
                          className="block w-full text-left px-4 py-2 text-sm hover:bg-gray-100"
                        >
                          {translatedText.metaverseDownload}
                        </button>
                      )}

                      <button
                        onClick={() => { 
                          localStorage.removeItem("user");
                          window.dispatchEvent(new CustomEvent("auth:logout"));
                          navigate("/login");
                        }}
                        className="block w-full text-left px-4 py-2 text-sm hover:bg-gray-100"
                      >
                        {translatedText.text14}
                      </button>
                    </>
                  ) : (
                    <>
                      <Link to="/login"    className="block px-4 py-2 text-sm hover:bg-gray-100 no-underline">{translatedText.text11}</Link>
                      <Link to="/register" className="block px-4 py-2 text-sm hover:bg-gray-100 no-underline">{translatedText.text12}</Link>
                    </>
                  )}
                </div>
              )}
            </div>

            {/* Hamburger (mobile/tablet) */}
            <button
              className="lg:hidden inline-flex items-center justify-center rounded-md p-2 text-black"
              onClick={() => setMobileMenuOpen((v) => !v)}
              aria-expanded={mobileMenuOpen}
              aria-label="Toggle menu"
            >
              ☰
            </button>
          </div>
        </div>
      </div>

      {/* Desktop cart popup */}
      {cartPopupVisible && (
        <div className="hidden lg:block absolute right-4 sm:right-6 top-full mt-2 w-80 z-[10100] bg-white rounded-xl shadow-lg p-4 text-left">
          <div className="text-sm font-semibold text-gray-800 mb-2">Perché acquistare il nostro prodotto?</div>
          <ul className="text-sm text-black space-y-2">
            <li>🔒 {translatedText.text16}</li>
            <li>✔️ {translatedText.text17}</li>
            <li>💬 {translatedText.text18}</li>
          </ul>
        </div>
      )}

      {/* Mobile menu panel */}
      {mobileMenuOpen && (
        <div className="lg:hidden absolute left-0 right-0 top-full bg-white shadow-md z-[10100] px-4 py-4 max-h-[calc(100dvh-5rem)] overflow-y-auto">
          <Link to="/"         onClick={() => setMobileMenuOpen(false)} className="block py-2 text-black font-semibold text-lg no-underline">{translatedText.text1}</Link>
          <Link to="/about"    onClick={() => setMobileMenuOpen(false)} className="block py-2 text-black font-semibold text-lg no-underline">{translatedText.text2}</Link>
          <Link to="/choose"   onClick={() => setMobileMenuOpen(false)} className="block py-2 text-black font-semibold text-lg no-underline">{translatedText.text3}</Link>
          <Link to="/FuoriPreview/127981"onClick={() => setMobileMenuOpen(false)} className="block py-2 text-black font-semibold text-lg no-underline">{translatedText.text4}</Link>
          <Link to="/community"   onClick={() => setMobileMenuOpen(false)} className="block py-2 text-black font-semibold text-lg no-underline">{translatedText.text5}</Link>
        {/*  <Link to="/blog"     onClick={() => setMobileMenuOpen(false)} className="block py-2 text-black font-semibold text-lg no-underline">{translatedText.text6}</Link>  */}
        {/*  <Link to="/nft"      onClick={() => setMobileMenuOpen(false)} className="block py-2 text-black font-semibold text-lg no-underline">{translatedText.text7}</Link>  */}
          <Link to="/support"  onClick={() => setMobileMenuOpen(false)} className="block py-2 text-black font-semibold text-lg no-underline">{translatedText.text8}</Link>

          <hr className="my-3" />

          {/* Language (mobile) */}
          <div className="flex items-center gap-2 py-1">
            <GlobeAltIcon className="h-5 w-5 text-black" />
            <select
              className="text-sm border rounded px-2 py-1 text-black"
              value={effectiveLangValue}
              onChange={handleHeaderLangChange}
            >
              <option value="en">{LANG_LABELS.en}</option>
              <option value="it">{LANG_LABELS.it}</option>
              <option value="fr">{LANG_LABELS.fr}</option>

            {extraLangOption && (
              <option value={extraLangOption}>{extraLangLabel}</option>
            )}
            </select>
          </div>

          <hr className="my-3" />

          {/* Friend Requests (mobile) */}
          {isLoggedIn && (
            <button
              onClick={() => { navigate("/friends"); setMobileMenuOpen(false); }}
              className="flex items-center gap-2 text-black font-semibold no-underline py-2 w-full"
            >
              <div className="relative">
                <UsersRound className="w-5 h-5" />
                {pendingRequests > 0 && (
                  <span className="absolute -top-1 -right-1 bg-red-500 text-white text-[10px] w-4 h-4 flex items-center justify-center rounded-full">
                    {pendingRequests}
                  </span>
                )}
              </div>
              <span>{translatedText.textRichieste}</span>
            </button>
          )}

          {/* Messages (mobile) */}
          {isLoggedIn && (
            <button
              onClick={() => navigate("/messages")}
              className="flex items-center gap-2 text-black font-semibold no-underline py-2 w-full"
            >
              <div className="relative">
                <MessageCircle className="w-5 h-5" />
                {unreadMessages > 0 && (
                  <span className="absolute -top-1 -right-1 flex h-4 w-4">
                    <span className="animate-ping absolute inline-flex h-full w-full rounded-full bg-red-400 opacity-75" />
                    <span className="relative inline-flex rounded-full h-4 w-4 bg-red-500 text-[10px] text-white items-center justify-center">
                      {unreadMessages}
                    </span>
                  </span>
                )}
              </div>
              <span>{translatedText.textMessaggi}</span>
            </button>
          )}
          
          {/* Cart (mobile) */}
          <Link
            to="/carrello"
            onClick={() => setMobileMenuOpen(false)}
            className="flex items-center gap-2 text-black font-semibold no-underline py-2"
          >
            <ShoppingCart className="w-5 h-5" />
            {translatedText.text9}
            {cartCount > 0 && (
              <span className="ml-auto bg-red-500 text-white text-xs w-5 h-5 flex items-center justify-center rounded-full">
                {cartCount}
              </span>
            )}
          </Link>

          <hr className="my-3" />

          {/* Account (mobile) */}
          {headerUser ? (
            <>
              <Link
                to="/profile"
                onClick={() => setMobileMenuOpen(false)}
                className="block py-2 text-sm text-black no-underline"
              >
                {translatedText.text13}
              </Link>
              <Link
                to="/myTours"
                onClick={() => setMobileMenuOpen(false)}
                className="block py-2 text-sm text-black no-underline"
              >
                {translatedText.text15}
              </Link>

              {hasPlan && (
                <button
                  onClick={() => {
                    setMobileMenuOpen(false);
                    openMetaverseModal();
                  }}
                  className="block w-full text-left py-2 text-sm text-black"
                >
                  {translatedText.metaverseDownload}
                </button>
              )}

              <button
                onClick={() => {
                  localStorage.removeItem("user");
                  window.dispatchEvent(new CustomEvent("auth:logout"));
                  setMobileMenuOpen(false);
                  navigate("/login");
                }}
                className="block text-left w-full py-2 text-sm text-black"
              >
                {translatedText.text14}
              </button>
            </>
          ) : (
            <>
              <Link
                to="/login"
                onClick={() => setMobileMenuOpen(false)}
                className="block py-2 text-sm text-black no-underline"
              >
                {translatedText.text11}
              </Link>
              <Link
                to="/register"
                onClick={() => setMobileMenuOpen(false)}
                className="block py-2 text-sm text-black no-underline"
              >
                {translatedText.text12}
              </Link>
            </>
          )}
        </div>
      )}

      {showMetaverseModal && (
      <div
        className="fixed inset-0 z-[99999] flex items-center justify-center bg-black/50 px-4"
        role="dialog"
        aria-modal="true"
      >
        <div className="w-full max-w-md rounded-xl bg-white shadow-2xl">
          
          {/* Header */}
          <div className="border-b px-6 py-4">
            <h3 className="text-lg font-bold text-black">
              {translatedText.metaverseModalTitle}
            </h3>
          </div>

          {/* Body */}
          <div className="px-6 py-5 space-y-4">
            <button
              onClick={() => setPreDownload("android")}
              className="w-full rounded-lg bg-green-600 text-white py-3 font-bold hover:opacity-90"
            >
              📱 {translatedText.metaverseAndroid}
            </button>

            <button
              onClick={() => setPreDownload("pc")}
              className="w-full rounded-lg bg-blue-600 text-white py-3 font-bold hover:opacity-90"
            >
              💻 {translatedText.metaversePC}
            </button>
          </div>

          {/* Footer */}
          <div className="border-t px-6 py-4 flex justify-end">
            <button
              onClick={closeMetaverseModal}
              className="px-4 py-2 rounded-lg border border-gray-300 text-black hover:bg-gray-50"
            >
              {translatedText.metaverseClose}
            </button>
          </div>
        </div>
      </div>
    )}

    {preDownload && (
      <div
        className="fixed inset-0 z-[100000] flex items-center justify-center bg-black/50 px-4"
        role="dialog"
        aria-modal="true"
      >
        <div className="w-full max-w-md rounded-xl bg-white shadow-2xl">
          <div className="border-b px-6 py-4">
            <h3 className="text-lg font-bold text-black">
              {preDownload === "android"
                ? translatedText.androidModalTitle
                : translatedText.pcModalTitle}
            </h3>
          </div>

          <div className="px-6 py-5">
            <p className="text-sm leading-relaxed text-gray-700 whitespace-pre-line">
              {preDownload === "android"
                ? translatedText.androidModalBody
                : translatedText.pcModalBody}
            </p>
          </div>

          <div className="border-t px-6 py-4 flex items-center justify-end gap-2">
            <button
              onClick={() => setPreDownload(null)}
              className="px-4 py-2 rounded-lg border border-gray-300 text-black hover:bg-gray-50"
            >
              {translatedText.cancel || "Cancel"}
            </button>

            <button
              onClick={preDownload === "android" ? downloadAndroid : downloadPC}
              className="px-4 py-2 rounded-lg bg-black text-white hover:opacity-90"
            >
              {translatedText.downloadNow || "Download"}
            </button>
          </div>
        </div>
      </div>
    )}
    </header>
    </>
  );
};

export default Header;
